import { LiksConfirmDialog } from '../../../components/liks/LiksConfirmDialog/LiksConfirmDialog';
import {
  KnowledgeDocumentPreviewModal,
  type LiksDocumentPreviewItem,
} from '../../../features/knowledge/components/KnowledgeDocumentPreviewModal';
import type { KnowledgeBaseDocument } from '../../../types/knowledge';
import type { WithResourceId } from '../../../types/knowledge';
import type { OperationProgress } from '../../../utils/operationProgress';
import { formatOperationProgressLabel } from '../../../utils/operationProgress';

type DocumentRow = WithResourceId<KnowledgeBaseDocument>;

type PendingDelete = {
  type: 'document';
  id: string;
  label: string;
} | null;

type BulkConfirm = {
  type: 'delete' | 'retry';
  documents: DocumentRow[];
} | null;

type KnowledgeDocumentsDialogsProps = {
  databaseId: string;
  pendingDelete: PendingDelete;
  bulkConfirm: BulkConfirm;
  previewItems: LiksDocumentPreviewItem[] | null;
  previewInitialIndex: number;
  isDeletingDocument: boolean;
  isBulkDeleting: boolean;
  bulkDeleteProgress: OperationProgress | null;
  isRetryDownloading: boolean;
  isRetryConfirming: boolean;
  isRetryConfirmDisabled: boolean;
  bulkRetryDownloadProgress: OperationProgress | null;
  bulkRetryProgress: OperationProgress | null;
  allRetryFilesPrepared: boolean;
  translate: (key: string, params?: Record<string, string>) => string;
  onConfirmDeleteDocument: () => void;
  onCancelPendingDelete: () => void;
  onConfirmBulkDelete: () => void;
  onConfirmBulkRetry: () => void;
  onDownloadBulkRetry: () => void;
  onCancelBulkConfirm: () => void;
  onClosePreview: () => void;
};

export function KnowledgeDocumentsDialogs({
  databaseId,
  pendingDelete,
  bulkConfirm,
  previewItems,
  previewInitialIndex,
  isDeletingDocument,
  isBulkDeleting,
  bulkDeleteProgress,
  isRetryDownloading,
  isRetryConfirming,
  isRetryConfirmDisabled,
  bulkRetryDownloadProgress,
  bulkRetryProgress,
  allRetryFilesPrepared,
  translate,
  onConfirmDeleteDocument,
  onCancelPendingDelete,
  onConfirmBulkDelete,
  onConfirmBulkRetry,
  onDownloadBulkRetry,
  onCancelBulkConfirm,
  onClosePreview,
}: KnowledgeDocumentsDialogsProps) {
  const bulkRetryCount = bulkConfirm?.documents.length ?? 0;
  const bulkDeleteCount = bulkConfirm?.documents.length ?? 0;
  const isRetryModalBusy = isRetryDownloading || isRetryConfirming;

  const retryDownloadLabel =
    bulkRetryCount === 1
      ? translate('knowledge.documentRetryDownload')
      : translate('knowledge.documentRetryDownloadBatch');

  return (
    <>
      <LiksConfirmDialog
        isOpen={pendingDelete?.type === 'document'}
        title={translate('knowledge.deleteDocumentTitle')}
        message={translate('knowledge.deleteDocumentMessage', {
          fileName: pendingDelete?.label ?? pendingDelete?.id ?? '',
        })}
        confirmLabel={
          isDeletingDocument
            ? translate('common.deleting')
            : translate('knowledge.deleteConfirm')
        }
        cancelLabel={translate('knowledge.deleteCancel')}
        isBusy={isDeletingDocument}
        onConfirm={onConfirmDeleteDocument}
        onCancel={onCancelPendingDelete}
      />

      <LiksConfirmDialog
        isOpen={bulkConfirm?.type === 'delete'}
        title={translate('knowledge.deleteBatchTitle', {
          count: String(bulkDeleteCount),
        })}
        message={translate('knowledge.deleteBatchMessage', {
          count: String(bulkDeleteCount),
        })}
        confirmLabel={
          isBulkDeleting
            ? formatOperationProgressLabel(
                translate('common.deleting'),
                bulkDeleteProgress,
                true,
              )
            : translate('knowledge.deleteConfirm')
        }
        cancelLabel={translate('knowledge.deleteCancel')}
        isBusy={isBulkDeleting}
        onConfirm={onConfirmBulkDelete}
        onCancel={onCancelBulkConfirm}
      />

      <LiksConfirmDialog
        isOpen={bulkConfirm?.type === 'retry'}
        title={
          bulkRetryCount === 1
            ? translate('knowledge.documentRetryConfirmTitle')
            : translate('knowledge.documentRetryConfirmBatchTitle', {
                count: String(bulkRetryCount),
              })
        }
        message={
          bulkRetryCount === 1
            ? translate('knowledge.documentRetryConfirmMessage')
            : translate('knowledge.documentRetryConfirmBatchMessage', {
                count: String(bulkRetryCount),
              })
        }
        confirmLabel={
          isRetryConfirming
            ? formatOperationProgressLabel(
                translate('knowledge.documentRetryConfirming'),
                bulkRetryProgress,
                true,
              )
            : translate('knowledge.documentRetryConfirm')
        }
        cancelLabel={translate('knowledge.documentRetryCancel')}
        isBusy={isRetryModalBusy}
        isConfirmBusy={isRetryConfirming}
        isConfirmDisabled={isRetryConfirmDisabled}
        secondaryAction={{
          label: isRetryDownloading
            ? formatOperationProgressLabel(
                translate('knowledge.documentRetryDownloading'),
                bulkRetryDownloadProgress,
                true,
              )
            : retryDownloadLabel,
          onClick: onDownloadBulkRetry,
          disabled: allRetryFilesPrepared,
          isBusy: isRetryDownloading,
        }}
        onConfirm={onConfirmBulkRetry}
        onCancel={onCancelBulkConfirm}
      />

      {previewItems != null && previewItems.length > 0 && (
        <KnowledgeDocumentPreviewModal
          isOpen
          knowledgeBaseId={databaseId}
          documentId={
            previewItems[previewInitialIndex]?.id ?? previewItems[0].id
          }
          title={
            previewItems[previewInitialIndex]?.title ?? previewItems[0].title
          }
          items={previewItems}
          initialIndex={previewInitialIndex}
          closeLabel={translate('platformChat.closeDocumentPreview')}
          loadingLabel={translate('platformChat.documentPreviewLoading')}
          errorLabel={translate('platformChat.documentPreviewError')}
          unavailableLabel={translate(
            'platformChat.documentPreviewUnavailable',
          )}
          notReadyLabel={translate('knowledge.documentPreviewNotReady')}
          onClose={onClosePreview}
        />
      )}
    </>
  );
}
